import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, Table, TableHead,
  TableRow, TableCell, TableBody, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Chip, Tooltip, Stack, InputAdornment,
  alpha, Divider, Avatar, Switch, FormControlLabel,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import AddIcon                from '@mui/icons-material/Add';
import EditIcon               from '@mui/icons-material/Edit';
import DeleteIcon             from '@mui/icons-material/DeleteOutline';
import SearchIcon             from '@mui/icons-material/Search';
import BeachAccessIcon        from '@mui/icons-material/BeachAccess';
import GavelIcon              from '@mui/icons-material/Gavel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon     from '@mui/icons-material/CancelOutlined';
import AssignmentLateIcon     from '@mui/icons-material/AssignmentLate';
import MedicalServicesIcon    from '@mui/icons-material/MedicalServices';
import FamilyRestroomIcon     from '@mui/icons-material/FamilyRestroom';
import CardGiftcardIcon       from '@mui/icons-material/CardGiftcard';
import AutorenewIcon          from '@mui/icons-material/Autorenew';
import VisibilityIcon         from '@mui/icons-material/Visibility';
import GridViewIcon           from '@mui/icons-material/GridView';
import TableRowsIcon          from '@mui/icons-material/TableRows';
import CloseIcon              from '@mui/icons-material/Close';
import LockIcon               from '@mui/icons-material/Lock';
import VerifiedUserIcon       from '@mui/icons-material/VerifiedUser';
import apiService             from '../services/apiServices';
import useConfirm from '../hooks/useConfirm';

/* ── constants ──────────────────────────────────────────────────────────────── */
const TYPE_COLORS = {
  annual:        { bg: '#EFF6FF', color: '#1D4ED8', label: 'Vjetor' },
  sick:          { bg: '#FFF7ED', color: '#C2410C', label: 'Mjekësor' },
  maternity:     { bg: '#FDF4FF', color: '#7E22CE', label: 'Lehoni' },
  paternity:     { bg: '#F0FDF4', color: '#15803D', label: 'Atësi' },
  unpaid:        { bg: '#F8FAFC', color: '#475569', label: 'Pa pagë' },
  marriage:      { bg: '#FEFCE8', color: '#A16207', label: 'Martesë' },
  bereavement:   { bg: '#FFF1F2', color: '#BE123C', label: 'Vdekje' },
  blood_donation:{ bg: '#ECFEFF', color: '#0E7490', label: 'Gjak' },
  public_holiday:{ bg: '#F0FDF4', color: '#166534', label: 'Festë' },
  sick_work:     { bg: '#FFF7ED', color: '#EA580C', label: 'Lëndim' },
};

const TYPE_ICONS = {
  annual:        <BeachAccessIcon sx={{ fontSize: 18 }} />,
  sick:          <MedicalServicesIcon sx={{ fontSize: 18 }} />,
  maternity:     <FamilyRestroomIcon sx={{ fontSize: 18 }} />,
  paternity:     <FamilyRestroomIcon sx={{ fontSize: 18 }} />,
  marriage:      <CardGiftcardIcon sx={{ fontSize: 18 }} />,
  bereavement:   <AutorenewIcon sx={{ fontSize: 18 }} />,
};

const EMPTY = { Id: 0, Code: '', Name: '', IsPaid: true, MaxDaysYear: 20, CarryOverDays: 0, RequiresCert: false, LpReference: '', IsSystem: false };

/* ── tiny helper components ─────────────────────────────────────────────────── */
function TypeBadge({ code }) {
  const c = TYPE_COLORS[code] || { bg: '#F1F5F9', color: '#334155' };
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      bgcolor: c.bg, color: c.color,
      px: 1.2, py: 0.35, borderRadius: 1.5,
      border: `1px solid ${alpha(c.color, 0.18)}`,
      fontWeight: 700, fontSize: '0.7rem',
    }}>
      {code}
    </Box>
  );
}

function YesNo({ value, yesLabel, noLabel }) {
  return value
    ? <Stack direction="row" alignItems="center" spacing={0.4}><CheckCircleOutlineIcon sx={{ fontSize: 14, color: '#16a34a' }} /><Typography variant="caption" sx={{ color: '#15803d', fontWeight: 600 }}>{yesLabel || 'Po'}</Typography></Stack>
    : <Stack direction="row" alignItems="center" spacing={0.4}><CancelOutlinedIcon     sx={{ fontSize: 14, color: '#94a3b8' }} /><Typography variant="caption" sx={{ color: '#64748b'            }}>{noLabel  || 'Jo'}</Typography></Stack>;
}

function StatCard({ icon, label, value, color, bg }) {
  return (
    <Paper variant="outlined" sx={{ flex: 1, minWidth: 140, px: 2.5, py: 2, borderRadius: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, borderColor: alpha(color, 0.18) }}>
      <Avatar sx={{ bgcolor: bg, color, width: 38, height: 38, borderRadius: 1.5 }}>{icon}</Avatar>
      <Box>
        <Typography variant="h6" fontWeight={800} color="text.primary" lineHeight={1.1}>{value}</Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>{label}</Typography>
      </Box>
    </Paper>
  );
}

/* ── main page ──────────────────────────────────────────────────────────────── */
export default function LeavePoliciesPage() {
  const [rows,     setRows]     = useState([]);
  const [form,     setForm]     = useState(EMPTY);
  const [dlgOpen,  setDlgOpen]  = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [viewRow,  setViewRow]  = useState(null);
  const [search,   setSearch]   = useState('');
  const [filterP,  setFilterP]  = useState('');   // '' | 'paid' | 'unpaid'
  const [viewMode, setViewMode] = useState('table');

  const load = useCallback(async () => {
    const r = await apiService().get('leaveTypes');
    setRows(r?.data || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() =>
    rows.filter(r => {
      if (search   && !r.Name.toLowerCase().includes(search.toLowerCase()) && !r.Code.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterP === 'paid'   && !r.IsPaid)  return false;
      if (filterP === 'unpaid' &&  r.IsPaid)  return false;
      return true;
    })
  , [rows, search, filterP]);

  const stats = useMemo(() => ({
    total:    rows.length,
    paid:     rows.filter(r => r.IsPaid).length,
    unpaid:   rows.filter(r => !r.IsPaid).length,
    withCert: rows.filter(r => r.RequiresCert).length,
  }), [rows]);

  const openAdd  = () => { setForm({ ...EMPTY }); setEditing(null); setDlgOpen(true); };
  const openEdit = (r) => { setForm({ ...r });    setEditing(r.Id); setDlgOpen(true); };

  const save = async () => {
    if (editing) await apiService().put('leaveTypes', { ...form, Id: editing });
    else         await apiService().post('leaveTypes', form);
    setDlgOpen(false); load();
  };

  const [confirm, confirmUI] = useConfirm();


  const del = async (id) => {
    const row = rows.find(r => r.Id === id);
    if (row?.IsSystem) { alert('Politikat e sistemit nuk mund të fshihen.'); return; }
    if (!(await confirm({ title: 'Fshi politikën e pushimit', message: 'Fshij këtë politikë pushimi?', confirmLabel: 'Fshi' }))) return;
    await apiService().delete('leaveTypes', id);
    load();
  };

  const ACCENT = '#7c3aed';

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar sx={{ bgcolor: alpha(ACCENT, 0.1), color: ACCENT, borderRadius: 2, width: 44, height: 44 }}>
            <BeachAccessIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={800} color="text.primary">Politikat e pushimeve</Typography>
            <Typography variant="caption" color="text.secondary">Kuotat vjetore, bartja e ditëve dhe baza ligjore për çdo lloj pushimi</Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none', boxShadow: 'none', bgcolor: ACCENT, '&:hover': { boxShadow: 'none', bgcolor: alpha(ACCENT, 0.88) } }}
        >
          Shto politikë
        </Button>
      </Stack>

      {/* Stats */}
      <Stack direction="row" spacing={1.5} mb={3} flexWrap="wrap" useFlexGap>
        <StatCard icon={<BeachAccessIcon sx={{ fontSize: 20 }} />}        label="Gjithsej politika"  value={stats.total}    color={ACCENT}    bg={alpha(ACCENT, 0.1)} />
        <StatCard icon={<CheckCircleOutlineIcon sx={{ fontSize: 20 }} />} label="Me pagë"             value={stats.paid}     color="#16a34a"   bg="#dcfce7" />
        <StatCard icon={<CancelOutlinedIcon sx={{ fontSize: 20 }} />}     label="Pa pagë"             value={stats.unpaid}   color="#94a3b8"   bg="#f1f5f9" />
        <StatCard icon={<AssignmentLateIcon sx={{ fontSize: 20 }} />}     label="Kërkon certifikatë" value={stats.withCert} color="#f59e0b"   bg="#fef3c7" />
      </Stack>

      {/* Toolbar */}
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, borderRadius: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Kërko politikë ose kod…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment> }}
            sx={{ width: 240 }}
          />
          <TextField select size="small" label="Pagesa" value={filterP} onChange={e => setFilterP(e.target.value)} sx={{ width: 140 }}>
            <MenuItem value="">Të gjitha</MenuItem>
            <MenuItem value="paid">Me pagë</MenuItem>
            <MenuItem value="unpaid">Pa pagë</MenuItem>
          </TextField>
          <Box flex={1} />
          <ToggleButtonGroup size="small" value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)}>
            <ToggleButton value="table" sx={{ px: 1.2 }}><Tooltip title="Tabelë"><TableRowsIcon sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
            <ToggleButton value="grid"  sx={{ px: 1.2 }}><Tooltip title="Kartat"><GridViewIcon  sx={{ fontSize: 16 }} /></Tooltip></ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      <Typography variant="caption" color="text.disabled" sx={{ mb: 1, display: 'block', pl: 0.5 }}>
        {filtered.length} {filtered.length === 1 ? 'rezultat' : 'rezultate'}
        {(filterP || search) && ' • filtruar'}
      </Typography>

      {/* ── TABLE VIEW ── */}
      {viewMode === 'table' && (
        <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                {['Kodi', 'Emri i politikës', 'Pagesa', 'Maks ditë/vit', 'Bartet', 'Kërkon cert.', 'Ref. ligjore', ''].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', py: 1.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 7 }}>
                    <Stack alignItems="center" spacing={1}>
                      <Avatar sx={{ bgcolor: '#F1F5F9', width: 52, height: 52 }}>
                        <BeachAccessIcon sx={{ fontSize: 26, color: '#CBD5E1' }} />
                      </Avatar>
                      <Typography variant="body2" color="text.disabled" fontWeight={500}>Nuk ka politika pushimi për të shfaqur</Typography>
                      <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAdd}
                        sx={{ mt: 1, textTransform: 'none', borderRadius: 1.5, borderColor: alpha(ACCENT, 0.4), color: ACCENT }}>
                        Shto politikën e parë
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r, i) => {
                const tc = TYPE_COLORS[r.Code] || { bg: '#F1F5F9', color: '#334155' };
                return (
                  <TableRow key={r.Id} hover sx={{ borderTop: i > 0 ? '1px solid #F1F5F9' : 'none', '&:hover': { bgcolor: alpha(ACCENT, 0.03) } }}>
                    <TableCell sx={{ py: 1.5 }}>
                      <TypeBadge code={r.Code} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Avatar sx={{ bgcolor: tc.bg, color: tc.color, width: 28, height: 28, borderRadius: 1, fontSize: 13 }}>
                          {TYPE_ICONS[r.Code] || <BeachAccessIcon sx={{ fontSize: 14 }} />}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={700} color="text.primary">{r.Name}</Typography>
                          {r.IsSystem && (
                            <Stack direction="row" alignItems="center" spacing={0.3} mt={0.2}>
                              <LockIcon sx={{ fontSize: 10, color: '#94a3b8' }} />
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>Sistem</Typography>
                            </Stack>
                          )}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell><YesNo value={r.IsPaid} yesLabel="Me pagë" noLabel="Pa pagë" /></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {r.MaxDaysYear >= 365 ? <Chip size="small" label="Pa limit" sx={{ bgcolor: '#F0FDF4', color: '#15803d', fontWeight: 700, fontSize: '0.7rem', height: 20 }} /> : `${r.MaxDaysYear} ditë`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {r.CarryOverDays > 0
                        ? <Chip size="small" label={`${r.CarryOverDays}d`} sx={{ bgcolor: '#EFF6FF', color: '#1D4ED8', fontWeight: 700, fontSize: '0.7rem', height: 20 }} />
                        : <Typography variant="caption" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell><YesNo value={r.RequiresCert} /></TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: r.LpReference ? 'normal' : 'italic' }}>
                        {r.LpReference || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 1.5 }}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Shiko"><IconButton size="small" onClick={() => setViewRow(r)} sx={{ color: '#64748b', '&:hover': { bgcolor: alpha(ACCENT, 0.08), color: ACCENT } }}><VisibilityIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        <Tooltip title="Edito"><IconButton size="small" onClick={() => openEdit(r)} sx={{ color: '#64748b', '&:hover': { bgcolor: '#EFF6FF', color: '#2563eb' } }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        {!r.IsSystem && (
                          <Tooltip title="Fshij"><IconButton size="small" onClick={() => del(r.Id)} sx={{ color: '#64748b', '&:hover': { bgcolor: '#FFF1F2', color: '#e11d48' } }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ── GRID VIEW ── */}
      {viewMode === 'grid' && (
        <Box>
          {filtered.length === 0 && (
            <Paper variant="outlined" sx={{ py: 7, borderRadius: 2.5, textAlign: 'center' }}>
              <Stack alignItems="center" spacing={1}>
                <Avatar sx={{ bgcolor: '#F1F5F9', width: 52, height: 52 }}>
                  <BeachAccessIcon sx={{ fontSize: 26, color: '#CBD5E1' }} />
                </Avatar>
                <Typography variant="body2" color="text.disabled" fontWeight={500}>Nuk ka politika pushimi</Typography>
              </Stack>
            </Paper>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
            {filtered.map(r => {
              const tc = TYPE_COLORS[r.Code] || { bg: '#F1F5F9', color: '#334155' };
              return (
                <Paper
                  key={r.Id} variant="outlined"
                  sx={{
                    p: 2.5, borderRadius: 2.5, cursor: 'pointer', transition: 'all 0.18s',
                    borderColor: alpha(tc.color, 0.18),
                    '&:hover': { boxShadow: `0 4px 20px ${alpha(tc.color, 0.13)}`, borderColor: alpha(tc.color, 0.4), transform: 'translateY(-1px)' },
                  }}
                  onClick={() => setViewRow(r)}
                >
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
                    <Avatar sx={{ bgcolor: tc.bg, color: tc.color, width: 40, height: 40, borderRadius: 1.5 }}>
                      {TYPE_ICONS[r.Code] || <BeachAccessIcon sx={{ fontSize: 20 }} />}
                    </Avatar>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {r.IsSystem && <Tooltip title="I sistemit"><LockIcon sx={{ fontSize: 14, color: '#94a3b8' }} /></Tooltip>}
                      <YesNo value={r.IsPaid} yesLabel="Me pagë" noLabel="Pa pagë" />
                    </Stack>
                  </Stack>

                  <Typography variant="body1" fontWeight={700} color="text.primary" mb={0.3} sx={{ lineHeight: 1.3 }}>{r.Name}</Typography>
                  <TypeBadge code={r.Code} />

                  <Divider sx={{ my: 1.5 }} />

                  <Stack spacing={0.8}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">Maks ditë/vit</Typography>
                      <Typography variant="caption" fontWeight={700} color="text.primary">
                        {r.MaxDaysYear >= 365 ? 'Pa limit' : `${r.MaxDaysYear} ditë`}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">Bartet</Typography>
                      <Typography variant="caption" fontWeight={700} color="text.primary">
                        {r.CarryOverDays > 0 ? `${r.CarryOverDays} ditë` : 'Jo'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">Kërkon cert.</Typography>
                      <YesNo value={r.RequiresCert} />
                    </Stack>
                    {r.LpReference && (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">Ref. ligjore</Typography>
                        <Typography variant="caption" fontWeight={600} color={tc.color}>{r.LpReference}</Typography>
                      </Stack>
                    )}
                  </Stack>

                  <Stack direction="row" spacing={0.3} justifyContent="flex-end" mt={1.5}>
                    <Tooltip title="Edito">
                      <IconButton size="small" onClick={e => { e.stopPropagation(); openEdit(r); }} sx={{ '&:hover': { bgcolor: '#EFF6FF', color: '#2563eb' } }}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                    {!r.IsSystem && (
                      <Tooltip title="Fshij">
                        <IconButton size="small" onClick={e => { e.stopPropagation(); del(r.Id); }} sx={{ '&:hover': { bgcolor: '#FFF1F2', color: '#e11d48' } }}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ── VIEW DIALOG ── */}
      <Dialog open={!!viewRow} onClose={() => setViewRow(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {viewRow && (() => {
          const tc = TYPE_COLORS[viewRow.Code] || { bg: '#F1F5F9', color: '#334155' };
          return (
            <>
              <DialogTitle sx={{ pb: 1, pr: 2 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: tc.bg, color: tc.color, width: 42, height: 42, borderRadius: 1.5 }}>
                      {TYPE_ICONS[viewRow.Code] || <BeachAccessIcon sx={{ fontSize: 20 }} />}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{viewRow.Name}</Typography>
                      <Stack direction="row" spacing={1} mt={0.5} alignItems="center">
                        <TypeBadge code={viewRow.Code} />
                        <YesNo value={viewRow.IsPaid} yesLabel="Me pagë" noLabel="Pa pagë" />
                        {viewRow.IsSystem && (
                          <Stack direction="row" alignItems="center" spacing={0.3}>
                            <LockIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>Sistem</Typography>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                  <IconButton size="small" onClick={() => setViewRow(null)} sx={{ mt: 0.5 }}><CloseIcon fontSize="small" /></IconButton>
                </Stack>
              </DialogTitle>
              <Divider />
              <DialogContent sx={{ pt: 2.5 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  {[
                    { label: 'Ditë maksimale / vit', value: viewRow.MaxDaysYear >= 365 ? 'Pa limit' : `${viewRow.MaxDaysYear} ditë` },
                    { label: 'Ditë të bartura', value: viewRow.CarryOverDays > 0 ? `${viewRow.CarryOverDays} ditë` : '—' },
                    { label: 'Me pagë', value: <YesNo value={viewRow.IsPaid} yesLabel="Po" noLabel="Jo" /> },
                    { label: 'Kërkon certifikatë', value: <YesNo value={viewRow.RequiresCert} /> },
                    { label: 'Referenca ligjore', value: viewRow.LpReference || '—' },
                    { label: 'Politikë sistemi', value: <YesNo value={viewRow.IsSystem} yesLabel="Po" noLabel="Jo" /> },
                  ].map(({ label, value }) => (
                    <Paper key={label} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.4}>{label}</Typography>
                      {typeof value === 'string'
                        ? <Typography variant="body2" fontWeight={700} color="text.primary">{value}</Typography>
                        : value}
                    </Paper>
                  ))}
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => { setViewRow(null); openEdit(viewRow); }} startIcon={<EditIcon />} sx={{ textTransform: 'none' }}>Edito</Button>
                <Button variant="contained" onClick={() => setViewRow(null)}
                  sx={{ textTransform: 'none', borderRadius: 2, bgcolor: ACCENT, boxShadow: 'none', '&:hover': { boxShadow: 'none', bgcolor: alpha(ACCENT, 0.88) } }}>
                  Mbyll
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ── ADD / EDIT DIALOG ── */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #F1F5F9', pb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar sx={{ bgcolor: alpha(ACCENT, 0.1), color: ACCENT, width: 36, height: 36, borderRadius: 1.5 }}>
              <BeachAccessIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>{editing ? 'Edito politikën e pushimit' : 'Politikë e re pushimi'}</Typography>
              <Typography variant="caption" color="text.secondary">{editing ? 'Ndrysho rregullat e kësaj politike' : 'Plotëso të dhënat e politikës së re'}</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Stack spacing={2.5} mt={0.5}>
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small" label="Kodi *" value={form.Code}
                onChange={e => setForm(p => ({ ...p, Code: e.target.value.toLowerCase().replace(/\s/g, '_') }))}
                placeholder="p.sh. annual"
                sx={{ flex: 1 }}
                InputProps={{ sx: { borderRadius: 1.5, fontFamily: 'monospace' } }}
                helperText="Shkronja të vogla, pa hapësira"
              />
              <TextField
                size="small" label="Emri *" value={form.Name}
                onChange={e => setForm(p => ({ ...p, Name: e.target.value }))}
                placeholder="p.sh. Pushim Vjetor"
                sx={{ flex: 2 }}
                InputProps={{ sx: { borderRadius: 1.5 } }}
              />
            </Stack>

            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small" label="Ditë maks/vit" type="number" value={form.MaxDaysYear}
                onChange={e => setForm(p => ({ ...p, MaxDaysYear: Number(e.target.value) }))}
                sx={{ flex: 1 }}
                InputProps={{ sx: { borderRadius: 1.5 } }}
                helperText="365 = pa limit"
              />
              <TextField
                size="small" label="Ditë të bartura" type="number" value={form.CarryOverDays}
                onChange={e => setForm(p => ({ ...p, CarryOverDays: Number(e.target.value) }))}
                sx={{ flex: 1 }}
                InputProps={{ sx: { borderRadius: 1.5 } }}
                helperText="0 = nuk bartet"
              />
            </Stack>

            <TextField
              size="small" label="Referenca ligjore" value={form.LpReference}
              onChange={e => setForm(p => ({ ...p, LpReference: e.target.value }))}
              placeholder="p.sh. Neni 32 LP"
              InputProps={{ sx: { borderRadius: 1.5 } }}
            />

            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
              <FormControlLabel
                control={<Switch checked={!!form.IsPaid} onChange={e => setForm(p => ({ ...p, IsPaid: e.target.checked }))} size="small" color="success" />}
                label={<Typography variant="body2" fontWeight={600}>Me pagë</Typography>}
              />
              <FormControlLabel
                control={<Switch checked={!!form.RequiresCert} onChange={e => setForm(p => ({ ...p, RequiresCert: e.target.checked }))} size="small" color="warning" />}
                label={<Typography variant="body2" fontWeight={600}>Kërkon certifikatë</Typography>}
              />
              <FormControlLabel
                control={<Switch checked={!!form.IsSystem} onChange={e => setForm(p => ({ ...p, IsSystem: e.target.checked }))} size="small" />}
                label={<Typography variant="body2" fontWeight={600}>Politikë sistemi</Typography>}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #F1F5F9', gap: 1 }}>
          <Button onClick={() => setDlgOpen(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>Anulo</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={!form.Code || !form.Name}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, bgcolor: ACCENT, boxShadow: 'none', '&:hover': { boxShadow: 'none', bgcolor: alpha(ACCENT, 0.88) } }}
          >
            {editing ? 'Ruaj ndryshimet' : 'Krijo politikën'}
          </Button>
        </DialogActions>
      </Dialog>
      {confirmUI}
    </Box>
  );
}
